import { apiClient } from './client';
import type { OwnerEntity, SpecMatchResult } from '@bhops/shared';

// docs/plan/team-dashboards-and-production-handoff/plan.md §6.3 / §7

// 개선계획 §2-3 — 배정이 끝나도 설치 완료 전까지 카드가 남는다. 서버가 계약을
// 두 단계로 내려준다: 'pending' = 아직 배정 필요, 'assigned' = 배정 끝 · 설치 대기.
export type ContractAssignStage = 'pending' | 'assigned';

// 카드를 펼쳤을 때 보여줄 배정된 로봇 1대. status 가 DEPLOYED 면 교체 불가
// (먼저 회수해야 함) — 그래서 목록 응답에 status 까지 필요하다.
export interface AssignedRobotRef {
  id: string;
  serialShort: string | null;
  serialLong: string | null;
  // 서버가 status 를 아직 안 내려주는 동안에는 reservedRobots 로 폴백하므로 optional.
  status?: string | null;
  // 옵션 3종(26-07-30) — 카드를 펼쳤을 때 "어느 로봇을 교체할지" 고르려면 번호만으론
  // 부족하다. 구버전 서버는 안 내려주므로 optional — 없으면 화면이 번호만 보여준다.
  modelId?: string;
  color?: string | null;
  variantValue?: string | null;
  sensorType?: string | null;
}

export interface PendingContract {
  id: string;
  humanId: string;
  scheduledInstallAt: string | null;
  // 계약일 — 인박스 '최신순' 정렬(2026-10-22). 서버가 대기 · 완료 행 모두에 싣는다.
  signedAt?: string | null;
  handoffStatus: string;
  // S1 Phase 11C (R2 #7) — 고객사 진행 상태 게이팅. customerStatus 는 합성/override
  // 적용 결과, assignable 은 contracted/install_ready/install_done 일 때만 true.
  customerStatus: string;
  assignable: boolean;
  // 개선계획 §2-3. 서버 롤아웃 전에는 undefined → handoffStatus 로 폴백한다
  // (AssignmentInboxPage.contractStage).
  stage?: ContractAssignStage;
  assignedRobots?: AssignedRobotRef[];
  customer: {
    id: string;
    displayName: string;
    legalName: string;
    // 개선계획 §2-1 — 고객사 카드의 '담당자' 칸. 미제공/미입력이면 '—'.
    primaryContactName?: string | null;
    // 추가수정(26-07-27) §1-3 — 카드 담당 칸의 새 값: 우리 회사 담당 영업
    // (고객사 마스터 '영업 담당'). 구버전 서버는 안 내려줄 수 있어 optional —
    // 미제공/미입력이면 화면이 '—' 로 폴백한다.
    salesOwnerCode?: string | null;
  };
  store: { id: string; name: string } | null;
  // stage 별로 서버 select 가 다르다 — 'assigned' 행은 경량 조회라 일부 필드가
  // 빠진 채 온다. 선택 필드로 두어 호출부가 반드시 방어하게 한다.
  lines?: Array<{
    id: string;
    modelId: string;
    quantity: number;
    model: { code: string; displayName: string };
    // 희망 사양(26-07-30) — 배정된 로봇이 계약 요청과 어긋나는지 표시하는 근거.
    // 구버전 서버는 안 내려주므로 optional(없으면 어긋남 표시를 하지 않는다).
    desiredColor?: string | null;
    desiredDirection?: string | null;
    desiredSensor?: string | null;
  }>;
  reservedRobots?: Array<{
    id: string;
    serialShort: string | null;
    serialLong: string | null;
    modelId?: string;
    color?: string | null;
    variantValue?: string | null;
    sensorType?: string | null;
  }>;
  productionAllocations?: Array<{
    id?: string;
    productionPlanId?: string;
    allocatedCount: number;
    productionPlan?: {
      id: string;
      humanId: string;
      estimatedCompletionAt: string;
      modelId: string;
    };
  }>;
}

// S2 Phase 2.5 — 계약 라인별 영업 희망 spec (제조 매칭 표시용).
export interface AssignmentLineDesired {
  // round5 #2c — 라인(버킷) 단위 집계: 식별자 + 배정/남음 카운트 동봉.
  lineId: string;
  modelId: string;
  modelCode: string | null;
  quantity: number;
  desiredColor: string | null;
  desiredDirection: string | null;
  desiredSensor: string | null;
  assignedCount: number;
  remainingCount: number;
}

export interface AssignmentCandidates {
  contract: {
    id: string;
    humanId: string;
    scheduledInstallAt: string | null;
    handoffStatus: string;
    ownerEntity: OwnerEntity;
  };
  required: Record<string, number>;
  assigned: Record<string, number>;
  remaining: Record<string, number>;
  bufferDays: number;
  // S2 Phase 2.5 — per-line 희망 spec.
  lines: AssignmentLineDesired[];
  candidates: {
    stockRobots: Array<{
      id: string;
      serialShort: string | null;
      serialLong: string | null;
      modelId: string;
      status: string;
      /**
       * 후보 로봇의 소유 법인. 오너 결정 2026-10-21(허니컴로보틱스 로봇도 비욘드허니컴 고객사에
       * 배치) — 비욘드허니컴 계약의 후보에 허니컴로보틱스 로봇이 섞여 와서, 화면이 법인 이름을
       * 붙이는 데 쓴다. 이 칸을 싣기 전의 서버 응답에는 없다(그때는 표시하지 않는다).
       */
      ownerEntity?: OwnerEntity;
      currentStoreId: string | null;
      manufactureCompletedAt: string | null;
      // S2 Phase 2.5 — 매칭 평가 사양 + tier.
      color: string | null;
      variantValue: string | null;
      sensorType: string | null;
      specMatch: SpecMatchResult;
      // 개선계획 §2-4 — 한 번도 고객 출고된 적 없는 로봇('신규' 배지).
      isNew?: boolean;
    }>;
    productionPlans: Array<{
      id: string;
      humanId: string;
      modelId: string;
      estimatedCompletionAt: string;
      plannedCount: number;
      availableCount: number;
      status: string;
      isFeasible: boolean;
      // S2 Phase 2.9 (§9.4) — plan spec + 희망 spec 매칭 tier.
      color: string | null;
      sensorType: string | null;
      variantValue: string | null;
      specMatch: SpecMatchResult;
    }>;
  };
}

// S2 Phase 2.5 — 로봇 상세에서의 역방향 배정 후보 계약.
export interface EligibleContractForRobot {
  contractId: string;
  humanId: string;
  customerDisplayName: string | null;
  scheduledInstallAt: string | null;
  remainingForModel: number;
}

export interface AssignSelection {
  kind: 'stock' | 'plan';
  robotId?: string;
  productionPlanId?: string;
  count?: number;
}

export const assignmentsApi = {
  // hasMore — 설치 대기 카드까지 남기면 목록이 길어지므로 서버가 상위 N건만
  // 내려준다. 잘렸다는 사실을 화면에서 숨기지 않는다.
  async list(): Promise<{ items: PendingContract[]; total: number; hasMore?: boolean }> {
    const { data } = await apiClient.get('/manufacturing/assignments');
    return data;
  },

  async candidates(contractId: string): Promise<AssignmentCandidates> {
    const { data } = await apiClient.get(`/manufacturing/assignments/${contractId}/candidates`);
    return data;
  },

  // isSwap — 교체(해제 후 재배정) 경로임을 서버에 알린다. 서버에 계약당 1회
  // 완료 알림 가드가 있어, 이 신호가 없으면 로봇이 바뀌어도 영업·필드에 아무
  // 통지가 가지 않는다(필드가 옛 시리얼 기준으로 설치를 나간다).
  async assign(
    contractId: string,
    selections: AssignSelection[],
    overrideFeasibility = false,
    isSwap = false,
  ) {
    const { data } = await apiClient.post(`/manufacturing/assignments/${contractId}/assign`, {
      selections,
      overrideFeasibility: overrideFeasibility ? 'true' : 'false',
      ...(isSwap ? { isSwap: true } : {}),
    });
    return data;
  },

  async release(contractId: string, robotId?: string, allocationId?: string) {
    const params = new URLSearchParams();
    if (robotId) params.set('robotId', robotId);
    if (allocationId) params.set('allocationId', allocationId);
    const { data } = await apiClient.post(
      `/manufacturing/assignments/${contractId}/release?${params.toString()}`,
    );
    return data;
  },

  // S2 Phase 2.5 — 로봇 상세 역방향 배정.
  async eligibleContractsForRobot(robotId: string): Promise<EligibleContractForRobot[]> {
    const { data } = await apiClient.get(
      `/manufacturing/assignments/robot/${robotId}/eligible-contracts`,
    );
    return data;
  },

  async assignRobotToContract(robotId: string, contractId: string) {
    const { data } = await apiClient.post(`/manufacturing/assignments/robot/${robotId}/assign`, {
      contractId,
    });
    return data;
  },
};
